import { useEffect, useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { SlidersHorizontal, X } from "lucide-react";
import api from "../lib/api";
import ProductCard from "../components/ProductCard";
import QuickView from "../components/QuickView";
import { FadeUp } from "../components/Reveal";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Slider } from "../components/ui/slider";
import { Checkbox } from "../components/ui/checkbox";

const COLORS = ["Ivory", "Blush", "Emerald", "Crimson", "Midnight", "Gold", "Rose"];
const FABRICS = ["Raw Silk", "Organza", "Georgette", "Banarasi Silk", "Velvet", "Chanderi", "Tulle"];
const SIZES = ["XS", "S", "M", "L", "XL"];

export default function Shop() {
  const [params, setParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [quickView, setQuickView] = useState(null);
  const [qvOpen, setQvOpen] = useState(false);
  const [mobileFilters, setMobileFilters] = useState(false);

  const [sort, setSort] = useState("featured");
  const [priceMax, setPriceMax] = useState(300000);
  const [selColors, setSelColors] = useState([]);
  const [selFabrics, setSelFabrics] = useState([]);
  const [selSize, setSelSize] = useState(null);

  const category = params.get("category") || "";
  const occasion = params.get("occasion") || "";
  const isNew = params.get("is_new") || "";
  const isBest = params.get("is_bestseller") || "";
  const search = params.get("search") || "";

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data));
  }, []);

  useEffect(() => {
    setLoading(true);
    const p = { sort, max_price: priceMax };
    if (category) p.category = category;
    if (occasion) p.occasion = occasion;
    if (isNew) p.is_new = true;
    if (isBest) p.is_bestseller = true;
    if (search) p.search = search;
    if (selSize) p.size = selSize;
    api.get("/products", { params: p }).then((r) => {
      setProducts(r.data);
      setLoading(false);
    });
  }, [category, occasion, isNew, isBest, search, sort, priceMax, selSize]);

  const filtered = useMemo(() => {
    return products.filter((p) => {
      if (selColors.length && !selColors.includes(p.color)) return false;
      if (selFabrics.length && !selFabrics.includes(p.fabric)) return false;
      return true;
    });
  }, [products, selColors, selFabrics]);

  const toggle = (arr, setArr, val) =>
    setArr(arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val]);

  const setCategoryParam = (slug) => {
    const np = new URLSearchParams(params);
    if (slug) np.set("category", slug);
    else np.delete("category");
    np.delete("is_new"); np.delete("is_bestseller");
    setParams(np);
  };

  const clearAll = () => {
    setParams({});
    setSelColors([]); setSelFabrics([]); setSelSize(null); setPriceMax(300000);
  };

  const title = search ? `“${search}”` : occasion ? occasion : isNew ? "New Arrivals" : isBest ? "Best Sellers"
    : category ? (categories.find((c) => c.slug === category)?.name || "Shop") : "All Pieces";

  const openQuickView = (p) => { setQuickView(p); setQvOpen(true); };

  const FilterPanel = () => (
    <div className="space-y-9" data-testid="filter-panel">
      <div>
        <p className="overline mb-4">Category</p>
        <div className="space-y-2.5">
          <button onClick={() => setCategoryParam("")} className={`block text-sm ${!category ? "text-ink font-medium" : "text-ink-soft hover:text-ink"}`}>All Pieces</button>
          {categories.map((c) => (
            <button key={c.slug} onClick={() => setCategoryParam(c.slug)} className={`block text-sm ${category === c.slug ? "text-ink font-medium" : "text-ink-soft hover:text-ink"}`} data-testid={`filter-cat-${c.slug}`}>
              {c.name}
            </button>
          ))}
        </div>
      </div>

      <div>
        <p className="overline mb-4">Size</p>
        <div className="flex flex-wrap gap-2">
          {SIZES.map((s) => (
            <button key={s} onClick={() => setSelSize(selSize === s ? null : s)} className={`h-9 w-11 border text-xs transition-colors ${selSize === s ? "border-ink bg-ink text-ivory" : "border-line hover:border-ink"}`} data-testid={`filter-size-${s}`}>{s}</button>
          ))}
        </div>
      </div>

      <div>
        <p className="overline mb-4">Colour</p>
        <div className="space-y-2.5">
          {COLORS.map((c) => (
            <label key={c} className="flex items-center gap-2.5 text-sm text-ink-soft cursor-pointer">
              <Checkbox checked={selColors.includes(c)} onCheckedChange={() => toggle(selColors, setSelColors, c)} data-testid={`filter-color-${c}`} />
              {c}
            </label>
          ))}
        </div>
      </div>

      <div>
        <p className="overline mb-4">Fabric</p>
        <div className="space-y-2.5">
          {FABRICS.map((f) => (
            <label key={f} className="flex items-center gap-2.5 text-sm text-ink-soft cursor-pointer">
              <Checkbox checked={selFabrics.includes(f)} onCheckedChange={() => toggle(selFabrics, setSelFabrics, f)} data-testid={`filter-fabric-${f}`} />
              {f}
            </label>
          ))}
        </div>
      </div>

      <div>
        <p className="overline mb-4">Max Price · ₹{priceMax.toLocaleString("en-IN")}</p>
        <Slider min={20000} max={300000} step={5000} value={[priceMax]} onValueChange={(v) => setPriceMax(v[0])} data-testid="filter-price" />
      </div>

      <button onClick={clearAll} className="overline link-underline text-gold-deep" data-testid="clear-filters">Clear all</button>
    </div>
  );

  return (
    <div className="pt-20" data-testid="shop-page">
      {/* header */}
      <div className="px-5 md:px-10 pt-14 pb-10 max-w-[1600px] mx-auto">
        <FadeUp><p className="overline text-gold-deep mb-4">The Collection</p></FadeUp>
        <div className="flex items-end justify-between flex-wrap gap-4">
          <h1 className="font-serif text-5xl md:text-7xl tracking-tight">{title}</h1>
          <div className="flex items-center gap-3">
            <button onClick={() => setMobileFilters(true)} className="lg:hidden flex items-center gap-2 border border-line px-4 py-2.5 overline" data-testid="mobile-filter-btn">
              <SlidersHorizontal size={14} /> Filters
            </button>
            <Select value={sort} onValueChange={setSort}>
              <SelectTrigger className="w-[180px] border-line rounded-none bg-transparent overline" data-testid="sort-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-ivory">
                <SelectItem value="featured">Featured</SelectItem>
                <SelectItem value="newest">Newest</SelectItem>
                <SelectItem value="price-asc">Price: Low to High</SelectItem>
                <SelectItem value="price-desc">Price: High to Low</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      <div className="px-5 md:px-10 max-w-[1600px] mx-auto grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-10 pb-28">
        <aside className="hidden lg:block sticky top-28 self-start">
          <FilterPanel />
        </aside>

        <div>
          <p className="text-sm text-ink-soft mb-6" data-testid="results-count">{filtered.length} pieces</p>
          {loading ? (
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-6">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="aspect-[3/4] bg-black/[0.05] animate-pulse" />
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <div className="py-32 text-center">
              <p className="font-serif text-3xl mb-3">Nothing matches — yet</p>
              <button onClick={clearAll} className="overline link-underline text-gold-deep">Reset filters</button>
            </div>
          ) : (
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-12 md:gap-x-6">
              {filtered.map((p, i) => (
                <ProductCard key={p.slug} product={p} index={i} onQuickView={openQuickView} />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* mobile filters */}
      <AnimatePresence>
        {mobileFilters && (
          <motion.div className="fixed inset-0 z-[70] bg-ivory p-6 overflow-y-auto lg:hidden" initial={{ x: "-100%" }} animate={{ x: 0 }} exit={{ x: "-100%" }} transition={{ ease: [0.22, 1, 0.36, 1] }}>
            <div className="flex justify-between items-center mb-8">
              <span className="font-serif text-3xl">Filters</span>
              <button onClick={() => setMobileFilters(false)}><X /></button>
            </div>
            <FilterPanel />
            <button onClick={() => setMobileFilters(false)} className="btn-ink w-full py-4 overline mt-10">Show {filtered.length} pieces</button>
          </motion.div>
        )}
      </AnimatePresence>

      <QuickView product={quickView} open={qvOpen} onOpenChange={setQvOpen} />
    </div>
  );
}
